import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { categories, newsCategories } from "@/mocks/site";
import { works } from "@/mocks/works";
import { articles } from "@/mocks/articles";
import { siteConfig } from "@/mocks/site";

function CategorySchema({ categoryName }: { categoryName: string }) {
  const webPageSchema = {
    "@type": "WebPage",
    name: `${categoryName} - ${siteConfig.name}`,
    url: `${siteConfig.url}/category/${categoryName}`,
    description: `风间由美${categoryName}作品列表`,
  };

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: categoryName },
    ],
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={webPageSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
    </>
  );
}

type TabType = "works" | "news";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<TabType>("works");

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug) || categories[0],
    [slug]
  );

  const filteredWorks = useMemo(() => {
    if (slug === "all" || !slug) return works;
    return works.filter((w) => w.category === slug);
  }, [slug]);

  const filteredNews = useMemo(() => {
    const catMapping: Record<string, string> = {
      all: "all",
      movie: "release",
      tv: "release",
      stage: "event",
      variety: "event",
    };
    const articleCat = catMapping[slug || "all"] || "all";
    if (articleCat === "all") return articles;
    return articles.filter((a) => a.category === articleCat);
  }, [slug]);

  const currentNewsCategory = useMemo(
    () => newsCategories.find((nc) => {
      const catMapping: Record<string, string> = {
        all: "all",
        movie: "release",
        tv: "release",
        stage: "event",
        variety: "event",
      };
      return nc.slug === (catMapping[slug || "all"] || "all");
    }) || newsCategories[0],
    [slug]
  );

  const pageTitle = `${currentCategory.name} - ${siteConfig.name}`;
  const pageDescription = currentCategory.description || `风间由美${currentCategory.name}作品列表`;

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={`风间由美${currentCategory.name},${siteConfig.keywords}`}
        canonicalUrl={`${siteConfig.url}/category/${slug}`}
        ogType="website"
      />
      <CategorySchema categoryName={currentCategory.name} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 pb-14">
        <Breadcrumb
          items={[
            { label: "首页", path: "/" },
            { label: currentCategory.name },
          ]}
        />

        <h1
          className="text-3xl md:text-4xl font-semibold text-foreground-950 mt-4 mb-2"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {currentCategory.name}
        </h1>
        <p className="text-sm text-foreground-600 mb-8">{currentCategory.description}</p>

        {/* Tab Switcher */}
        <div className="flex items-center gap-2 mb-8">
          <button
            onClick={() => setActiveTab("works")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              activeTab === "works"
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
            }`}
          >
            作品列表
          </button>
          <button
            onClick={() => setActiveTab("news")}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
              activeTab === "news"
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
            }`}
          >
            相关动态
          </button>
        </div>

        {/* Category Sidebar */}
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="w-full lg:w-48 flex-shrink-0">
            <h4 className="text-sm font-medium text-foreground-950 mb-3 whitespace-nowrap">作品分类</h4>
            <div className="flex lg:flex-col flex-wrap gap-1.5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`text-sm px-3 py-2 rounded-lg whitespace-nowrap cursor-pointer transition-colors ${
                    slug === cat.slug
                      ? "bg-primary-100 text-primary-700 font-medium"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {activeTab === "works" && (
              <>
                {filteredWorks.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-foreground-500">暂无该分类下的作品</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {filteredWorks.map((work) => (
                      <div
                        key={work.id}
                        className="group cursor-pointer bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all"
                      >
                        <div className="relative aspect-[3/4] overflow-hidden">
                          <img
                            src={work.image}
                            alt={`风间由美 ${work.title}`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            title={`风间由美 ${work.title}`}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                            <p className="text-white/90 text-xs leading-relaxed line-clamp-3">{work.description}</p>
                          </div>
                        </div>
                        <div className="p-3">
                          <h3 className="text-sm font-medium text-foreground-950 truncate">{work.title}</h3>
                          <p className="text-xs text-foreground-500 mt-0.5">
                            {work.year} · {work.role}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <i className="ri-star-fill text-xs text-accent-500"></i>
                            <span className="text-xs text-foreground-600">{work.rating}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === "news" && (
              <>
                {filteredNews.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-foreground-500">暂无该分类下的动态</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {filteredNews.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="group cursor-pointer flex flex-col sm:flex-row gap-4 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-all"
                      >
                        <div className="w-full sm:w-48 h-32 flex-shrink-0 overflow-hidden rounded-lg">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            title={`风间由美 ${article.title}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xs text-accent-500 bg-accent-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                              {article.tags[0]}
                            </span>
                            <span className="text-xs text-foreground-500">{article.date}</span>
                          </div>
                          <h3 className="text-base font-medium text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                            {article.summary}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}